import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Menu,
  MenuItem,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useViewModeStore } from '../../store/viewModeStore';
import {
  Logout as LogoutIcon,
  AccountCircle,
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Assignment as AssignmentIcon,
  Map as MapIcon,
  PersonOff as PersonOffIcon,
  People as PeopleIcon,
  AdminPanelSettings as AdminIcon,
  Engineering as InspectorIcon,
  SupervisedUserCircle as ImpersonateIcon
} from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { inspectionService } from '../../services/inspectionService';
import {
  getEffectiveRole,
  canSwitchToInspectorView,
  canViewDashboard,
  canManageUsers
} from '../../utils/permissions';

export function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, impersonatedBy, impersonate, exitImpersonation } = useAuthStore();
  const { viewMode, setViewMode } = useViewModeStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [impersonateAnchor, setImpersonateAnchor] = useState<null | HTMLElement>(null);
  const [inspectors, setInspectors] = useState<Array<{ id: number; name: string; email?: string }>>([]);

  useEffect(() => {
    if (user?.role === 'admin' && !impersonatedBy) {
      inspectionService.getInspectors().then(setInspectors).catch(console.error);
    }
  }, [user?.role, impersonatedBy]);

  // Check if user can switch modes (admin or staff with isAlsoInspector)
  const canSwitchMode = canSwitchToInspectorView(user);

  // Determine effective role based on viewMode
  const effectiveRole = getEffectiveRole(user, viewMode);

  // Define navigation routes based on effective role.
  // Staff manages inspections but has no Dashboard, Per Diem or Users.
  const routes = [
    ...(canViewDashboard(effectiveRole) ? [
      { label: 'Dashboard', path: '/dashboard' }
    ] : []),
    { label: 'Inspections', path: '/inspections' },
    { label: 'Map', path: '/map' },
    { label: 'Unavailability', path: '/unavailability' },
    ...(canManageUsers(effectiveRole) ? [
      { label: 'Users', path: '/users' }
    ] : [])
  ];

  const currentTab = routes.findIndex(route => route.path === location.pathname);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/login');
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    handleMobileMenuClose();
  };

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          {/* Mobile Hamburger Menu Button */}
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleMobileMenuToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo and Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img
              src="/assets/logo-dark.png"
              alt="Walker Blue"
              style={{ height: '40px', marginRight: '16px' }}
            />
            <Typography variant="h6" component="div" sx={{ display: { xs: 'none', sm: 'block' } }}>
              Inspection Management System
            </Typography>
          </Box>

          {/* Mobile View Mode Toggle - in AppBar for admins with isAlsoInspector */}
          {canSwitchMode && isMobile && (
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, newMode) => newMode && setViewMode(newMode)}
              size="small"
              sx={{
                bgcolor: 'rgba(255,255,255,0.1)',
                '& .MuiToggleButton-root': {
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.3)',
                  py: 0.5,
                  px: 1.5,
                  '&.Mui-selected': {
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.3)',
                    }
                  },
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                  }
                }
              }}
            >
              <ToggleButton value="admin">
                <AdminIcon fontSize="small" sx={{ mr: 0.5 }} />
                {user?.role === 'staff' ? 'Staff' : 'Admin'}
              </ToggleButton>
              <ToggleButton value="inspector">
                <InspectorIcon fontSize="small" sx={{ mr: 0.5 }} />
                Inspector
              </ToggleButton>
            </ToggleButtonGroup>
          )}

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Tabs value={currentTab >= 0 ? currentTab : false} textColor="inherit" indicatorColor="secondary">
                {routes.map((route, index) => (
                  <Tab key={route.path} value={index} label={route.label} component={Link} to={route.path} />
                ))}
              </Tabs>

              {user && (
                <>
                  <Button
                    color="inherit"
                    onClick={handleMenuOpen}
                    startIcon={<AccountCircle />}
                    sx={{ ml: 1 }}
                  >
                    {user.name} ({user.role})
                  </Button>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
                    {user.role === 'admin' && !impersonatedBy && (
                      <MenuItem onClick={(e) => { handleMenuClose(); setImpersonateAnchor(e.currentTarget); }}>
                        <ImpersonateIcon sx={{ mr: 1 }} fontSize="small" />
                        Impersonate Inspector
                      </MenuItem>
                    )}
                    <MenuItem onClick={handleLogout}>
                      <LogoutIcon sx={{ mr: 1 }} fontSize="small" />
                      Logout
                    </MenuItem>
                  </Menu>

                  {/* Impersonate Inspector Menu */}
                  <Menu
                    anchorEl={impersonateAnchor}
                    open={Boolean(impersonateAnchor)}
                    onClose={() => setImpersonateAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    {inspectors.map((insp) => (
                      <MenuItem
                        key={insp.id}
                        onClick={async () => {
                          setImpersonateAnchor(null);
                          try {
                            await impersonate(String(insp.id));
                            navigate('/inspections');
                          } catch (err) {
                            console.error('Impersonation failed:', err);
                          }
                        }}
                      >
                        {insp.name}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Impersonation Banner */}
      {impersonatedBy && (
        <Box
          sx={{
            bgcolor: 'error.main',
            color: 'white',
            py: 0.5,
            px: 2,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Typography variant="body2" fontWeight="bold">
            Viewing as: {user?.name} ({user?.role})
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => { exitImpersonation(); navigate('/inspections'); }}
            sx={{ color: 'white', borderColor: 'white', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
          >
            Exit Impersonation
          </Button>
        </Box>
      )}

      {/* View Mode Toggle Bar - Only for admins with isAlsoInspector */}
      {canSwitchMode && !isMobile && (
        <Box
          sx={{
            bgcolor: 'grey.100',
            py: 1,
            px: 3,
            display: 'flex',
            justifyContent: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => newMode && setViewMode(newMode)}
            size="small"
          >
            <ToggleButton value="admin" sx={{ px: 3 }}>
              <AdminIcon sx={{ mr: 1 }} fontSize="small" />
              {user?.role === 'staff' ? 'Staff' : 'Admin'}
            </ToggleButton>
            <ToggleButton value="inspector" sx={{ px: 3 }}>
              <InspectorIcon sx={{ mr: 1 }} fontSize="small" />
              Inspector
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Mobile Drawer Menu */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
      >
        <Box sx={{ width: 280 }}>
          {/* User Info Section */}
          {user && (
            <>
              <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AccountCircle sx={{ fontSize: 40, mr: 1.5 }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {user.name}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      {user.role}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Divider />
            </>
          )}

          {/* Navigation Items */}
          <List>
            {canViewDashboard(effectiveRole) && (
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleNavigate('/dashboard')}>
                  <ListItemIcon>
                    <DashboardIcon />
                  </ListItemIcon>
                  <ListItemText primary="Dashboard" />
                </ListItemButton>
              </ListItem>
            )}

            <ListItem disablePadding>
              <ListItemButton onClick={() => handleNavigate('/inspections')}>
                <ListItemIcon>
                  <AssignmentIcon />
                </ListItemIcon>
                <ListItemText primary="Inspections" />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton onClick={() => handleNavigate('/map')}>
                <ListItemIcon>
                  <MapIcon />
                </ListItemIcon>
                <ListItemText primary="Map" />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton onClick={() => handleNavigate('/unavailability')}>
                <ListItemIcon>
                  <PersonOffIcon />
                </ListItemIcon>
                <ListItemText primary="Unavailability" />
              </ListItemButton>
            </ListItem>

            {canManageUsers(effectiveRole) && (
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleNavigate('/users')}>
                  <ListItemIcon>
                    <PeopleIcon />
                  </ListItemIcon>
                  <ListItemText primary="Users" />
                </ListItemButton>
              </ListItem>
            )}
          </List>

          <Divider />

          {/* Logout */}
          <List>
            <ListItem disablePadding>
              <ListItemButton onClick={() => {
                handleMobileMenuClose();
                handleLogout();
              }}>
                <ListItemIcon>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </>
  );
}
